// src/frontend_react/src/components/ui/Box/Box.js
import React from 'react';
import styles from './Box.module.css';

// Helper to map spacing/theme tokens to CSS variable strings
const getTokenValue = (value, tokenPrefix, unitFallback = 'px') => {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'number') return `calc(var(--cipher-spacing-unit) * ${value})`;
  if (typeof value === 'string') {
    if (['xs', 'sm', 'md', 'lg', 'xl', 'xxl'].includes(value)) {
      return `var(--cipher-${tokenPrefix}-${value})`;
    }
    return value;
  }
  return undefined;
};


const Box = ({
  as: Component = 'div',
  children,
  className = '',
  style,
  m, mt, mr, mb, ml, mx, my,
  p, pt, pr, pb, pl, px, py,
  width, height, maxWidth, maxHeight, minWidth, minHeight,
  bg, borderColor, borderWidth, borderStyle, borderRadius,
  flexGrow, flexShrink, flexBasis, alignSelf,
  display, flexDirection, alignItems, justifyContent, flexWrap, gap,
  boxShadow, textAlign, overflow,
  position, top, right, bottom, left, zIndex,
  ...rest 
}) => {
  const boxStyle = { ...style };

  // Spacing
  if (m !== undefined) boxStyle.margin = getTokenValue(m, 'spacing');
  if (mt !== undefined) boxStyle.marginTop = getTokenValue(mt, 'spacing');
  if (mr !== undefined) boxStyle.marginRight = getTokenValue(mr, 'spacing');
  if (mb !== undefined) boxStyle.marginBottom = getTokenValue(mb, 'spacing');
  if (ml !== undefined) boxStyle.marginLeft = getTokenValue(ml, 'spacing');
  if (mx !== undefined) {
    boxStyle.marginLeft = getTokenValue(mx, 'spacing');
    boxStyle.marginRight = getTokenValue(mx, 'spacing');
  }
  if (my !== undefined) {
    boxStyle.marginTop = getTokenValue(my, 'spacing');
    boxStyle.marginBottom = getTokenValue(my, 'spacing');
  }

  if (p !== undefined) boxStyle.padding = getTokenValue(p, 'spacing');
  if (pt !== undefined) boxStyle.paddingTop = getTokenValue(pt, 'spacing');
  if (pr !== undefined) boxStyle.paddingRight = getTokenValue(pr, 'spacing');
  if (pb !== undefined) boxStyle.paddingBottom = getTokenValue(pb, 'spacing');
  if (pl !== undefined) boxStyle.paddingLeft = getTokenValue(pl, 'spacing');
  if (px !== undefined) {
    boxStyle.paddingLeft = getTokenValue(px, 'spacing');
    boxStyle.paddingRight = getTokenValue(px, 'spacing');
  }
  if (py !== undefined) {
    boxStyle.paddingTop = getTokenValue(py, 'spacing');
    boxStyle.paddingBottom = getTokenValue(py, 'spacing');
  }

  // Dimensions
  if (width !== undefined) boxStyle.width = getTokenValue(width, 'size', 'auto');
  if (height !== undefined) boxStyle.height = getTokenValue(height, 'size', 'auto');
  if (maxWidth !== undefined) boxStyle.maxWidth = getTokenValue(maxWidth, 'size', 'none');
  if (maxHeight !== undefined) boxStyle.maxHeight = getTokenValue(maxHeight, 'size', 'none');
  if (minWidth !== undefined) boxStyle.minWidth = getTokenValue(minWidth, 'size', '0');
  if (minHeight !== undefined) boxStyle.minHeight = getTokenValue(minHeight, 'size', '0');

  // Background & Border
  if (bg !== undefined) boxStyle.backgroundColor = getTokenValue(bg, 'color-background');
  if (borderColor !== undefined) boxStyle.borderColor = getTokenValue(borderColor, 'color-border');
  if (borderWidth !== undefined) boxStyle.borderWidth = getTokenValue(borderWidth, 'border-width', 'px');
  if (borderStyle !== undefined) boxStyle.borderStyle = borderStyle;
  if (borderRadius !== undefined) boxStyle.borderRadius = getTokenValue(borderRadius, 'border-radius');

  // Flexbox Children Props
  if (flexGrow !== undefined) boxStyle.flexGrow = flexGrow;
  if (flexShrink !== undefined) boxStyle.flexShrink = flexShrink;
  if (flexBasis !== undefined) boxStyle.flexBasis = flexBasis;
  if (alignSelf !== undefined) boxStyle.alignSelf = alignSelf;
  
  // Flexbox Container Props
  if (display !== undefined) boxStyle.display = display;
  if (flexDirection !== undefined) boxStyle.flexDirection = flexDirection;
  if (alignItems !== undefined) boxStyle.alignItems = alignItems;
  if (justifyContent !== undefined) boxStyle.justifyContent = justifyContent;
  if (flexWrap !== undefined) boxStyle.flexWrap = flexWrap;
  if (gap !== undefined) boxStyle.gap = getTokenValue(gap, 'spacing');

  // Other styles
  if (boxShadow !== undefined) boxStyle.boxShadow = getTokenValue(boxShadow, 'shadow');
  if (textAlign !== undefined) boxStyle.textAlign = textAlign;
  if (overflow !== undefined) boxStyle.overflow = overflow;
  if (position !== undefined) boxStyle.position = position;
  if (top !== undefined) boxStyle.top = getTokenValue(top, 'spacing');
  if (right !== undefined) boxStyle.right = getTokenValue(right, 'spacing');
  if (bottom !== undefined) boxStyle.bottom = getTokenValue(bottom, 'spacing');
  if (left !== undefined) boxStyle.left = getTokenValue(left, 'spacing');
  if (zIndex !== undefined) boxStyle.zIndex = zIndex;


  return (
    <Component className={`${styles.boxBase} ${className}`} style={boxStyle} {...rest}>
      {children}
    </Component>
  );
};

export default Box;